// Hex grid foundation — flat-top hexagons in axial coordinates (q, r).
// Pure math + a tile-geometry factory; no scene/state dependencies, so it's
// unit-testable on its own. Everything spatial (paddy tiles, plot ↔ world,
// building placement, click-to-tile, cat bounds) builds on this.
//
// Layout: FLAT-TOP hexes. A tile's two pointy corners face ±X; its flat edges
// face roughly ±Z. Columns step in X; rows step in Z (with a half-row stagger).
//
//   world.x = size * 3/2 * q
//   world.z = size * sqrt(3) * (r + q/2)
//
// The field is the set of all hexes within FIELD_RADIUS of the center (0,0)
// using cube distance — a big hexagon of hexagons.

import * as THREE from 'three'

export const HEX_SIZE = 1.64      // center-to-corner radius (world units) — 2× larger tiles
export const FIELD_RADIUS = 6     // → 1 + 3·R·(R+1) = 127 tiles
export const TILE_HEIGHT = 0.12   // prism thickness (top sits at +TILE_HEIGHT/2)
// Walkable surface height — the top of the tile dioramas' ground base. All
// characters (hero, peasants, goons) stand at this Y so they don't sink into
// the raised tile bases. Tune if the diorama base height changes.
export const GROUND_Y = 0.32

const SQRT3 = Math.sqrt(3)

// Axial (q,r) → world {x, z} (POINTY-top — matches the low-poly tile dioramas,
// whose ground hexagons are pointy-top: points face ±Z, flat edges face ±X).
export function axialToWorld(q, r, size = HEX_SIZE) {
  return {
    x: size * SQRT3 * (q + r / 2),
    z: size * 1.5 * r,
  }
}

// World {x, z} → nearest axial {q, r} (pointy-top inverse + cube rounding).
export function worldToAxial(x, z, size = HEX_SIZE) {
  const qf = ((SQRT3 / 3) * x - (1 / 3) * z) / size
  const rf = ((2 / 3) * z) / size
  return roundAxial(qf, rf)
}

// Round fractional axial to the nearest hex (cube-coordinate rounding).
export function roundAxial(qf, rf) {
  const sf = -qf - rf
  let q = Math.round(qf)
  let r = Math.round(rf)
  let s = Math.round(sf)
  const dq = Math.abs(q - qf)
  const dr = Math.abs(r - rf)
  const ds = Math.abs(s - sf)
  if (dq > dr && dq > ds) q = -r - s
  else if (dr > ds) r = -q - s
  return { q, r }
}

// Cube distance between two axial coords (number of hex steps).
export function hexDistance(a, b) {
  return (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(a.q + a.r - b.q - b.r)) / 2
}

// The 6 flat-top neighbor directions.
const DIRS = [
  { q: 1, r: 0 }, { q: 1, r: -1 }, { q: 0, r: -1 },
  { q: -1, r: 0 }, { q: -1, r: 1 }, { q: 0, r: 1 },
]
export function neighbors(q, r) {
  return DIRS.map((d) => ({ q: q + d.q, r: r + d.r }))
}

// All hexes within `radius` of center (0,0) — the playable field.
export function fieldCoords(radius = FIELD_RADIUS) {
  const out = []
  for (let q = -radius; q <= radius; q++) {
    const rLo = Math.max(-radius, -q - radius)
    const rHi = Math.min(radius, -q + radius)
    for (let r = rLo; r <= radius && r <= rHi; r++) out.push({ q, r })
  }
  return out
}

// Is an axial coord inside the field?
export function inField(q, r, radius = FIELD_RADIUS) {
  return hexDistance({ q, r }, { q: 0, r: 0 }) <= radius
}

// Is a WORLD (x,z) position on the tile field? (rounds to the nearest tile and
// checks it's within the hex field) — used to keep characters on the tiles.
export function isInFieldWorld(x, z, radius = FIELD_RADIUS) {
  const { q, r } = worldToAxial(x, z)
  return inField(q, r, radius)
}

// A random in-field world position (a random tile center) — for spawning /
// wandering citizens so they never target an off-field point.
export function randomFieldPoint() {
  const all = fieldCoords()
  const c = all[Math.floor(Math.random() * all.length)]
  return axialToWorld(c.q, c.r)
}

// Stable string key for a tile (used as the plots-map key, replacing the old
// numeric tileIndex). Round-trips via parseKey().
export function key(q, r) { return `${q},${r}` }
export function parseKey(k) {
  const [q, r] = String(k).split(',').map(Number)
  return { q, r }
}

// Hex coords whose distance from the centre falls in [minD, maxD] — the rings around the field (the land that surrounds it).
export function ringCoords(minD, maxD) {
  const out = []
  for (let q = -maxD; q <= maxD; q++) {
    for (let r = Math.max(-maxD, -q - maxD); r <= Math.min(maxD, -q + maxD); r++) {
      const d = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2
      if (d >= minD && d <= maxD) out.push({ q, r })
    }
  }
  return out
}

// Cheap stable hash of a string → [0,1), for deterministic decoration (the same tile always looks the same).
export function hash01(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) }
  return ((h >>> 0) % 100000) / 100000
}

// Half-extent of the field in world units (for clamping the cat inside it).
export function fieldRadiusWorld(radius = FIELD_RADIUS, size = HEX_SIZE) {
  // Pointy-top: widest span is along X (corner hex reaches |x| ≈ √3·size·radius).
  return size * SQRT3 * radius + size
}

// A POINTY-TOP hexagonal prism geometry, sized to HEX_SIZE. Three's
// CylinderGeometry(r,r,h,6) is pointy-top by default (vertex on +Z, flat edges
// on ±X) — exactly what the pointy-top axial layout + the tile dioramas use, so
// neighbors share full straight edges with no gaps. No rotation needed.
export function createHexTileGeometry(size = HEX_SIZE, height = TILE_HEIGHT) {
  return new THREE.CylinderGeometry(size, size, height, 6)
}

// Matching pointy-top hexagon OUTLINE (top rim), for a 1px grid line on each
// tile. Returns a closed LineLoop BufferGeometry at the tile's top face.
export function createHexOutlineGeometry(size = HEX_SIZE, height = TILE_HEIGHT) {
  const pts = []
  const y = height / 2 + 0.002 // a hair above the top face to avoid z-fighting
  for (let i = 0; i < 6; i++) {
    // Vertices at +Z, +60°, … — matching CylinderGeometry (x = r·sinθ, z = r·cosθ).
    const a = (i * Math.PI) / 3
    pts.push(new THREE.Vector3(Math.sin(a) * size, y, Math.cos(a) * size))
  }
  pts.push(pts[0].clone()) // close the loop
  return new THREE.BufferGeometry().setFromPoints(pts)
}

// Convenience: world position of a tile's TOP face center (where props/buildings
// and the cat stand).
export function tileTopWorld(q, r, size = HEX_SIZE, height = TILE_HEIGHT) {
  const { x, z } = axialToWorld(q, r, size)
  return { x, y: height / 2, z }
}
